import { useMemo } from 'react';
import { useConfirmModalStore } from '@/stores';
import { Modal } from 'antd';
import { useTranslation } from 'react-i18next';
import { WarningModalIcon, InfoModalIcon, ErrorModalIcon, SuccessModalIcon } from '../Icons';

export enum ConfirmTypeEnum {
  WARNING,
  SUCCESS,
  Error,
  INFO,
  DESTRUCTIVE,
}

export const ConfirmModal = () => {
  const {
    isOpen,
    title,
    content,
    description,
    approveText,
    cancelText,
    onConfirm,
    onCancel,
    closeModal,
    confirmType = ConfirmTypeEnum.WARNING,
  } = useConfirmModalStore();

  const { t } = useTranslation();

  const modalData = [
    { headerTitle: null, icon: <WarningModalIcon /> },
    { headerTitle: 'SUCCESS', icon: <SuccessModalIcon /> },
    { headerTitle: 'ATTENTION', icon: <ErrorModalIcon /> },
    { headerTitle: null, icon: <InfoModalIcon /> },
  ];

  const getModalTitle = useMemo(() => {
    if (confirmType === ConfirmTypeEnum.DESTRUCTIVE || !modalData[confirmType]) {
      return null;
    }
    if (modalData[confirmType].headerTitle) {
      return (
        <div className='pb-4 border-b border-solid border-[rgb(232,230,227)]'>
          {modalData[confirmType].headerTitle}
        </div>
      );
    }
    return <div className='h-6' />;
  }, [confirmType]);

  const handleConfirm = () => {
    if (onConfirm) onConfirm();
    closeModal();
  };

  const handleCancel = () => {
    if (onCancel) onCancel();
    closeModal();
  };

  // Destructive confirmation — clean dialog
  if (confirmType === ConfirmTypeEnum.DESTRUCTIVE) {
    return (
      <Modal
        title={null}
        open={isOpen}
        closable={false}
        centered
        width={480}
        styles={{
          content: { borderRadius: 16, padding: 24 },
          body: { padding: 0 },
          footer: { border: 'none', padding: 0, marginTop: 16 },
        }}
        footer={
          <div className='flex justify-end gap-3'>
            <button
              type='button'
              className='h-10 px-5 rounded-lg border border-solid border-[rgb(232,230,227)] bg-white text-[rgb(57,57,60)] text-[13px] font-semibold cursor-pointer hover:bg-[rgb(245,245,243)] transition-colors'
              onClick={handleCancel}
            >
              {cancelText ?? t('cancel')}
            </button>
            <button
              type='button'
              className='h-10 px-5 rounded-lg bg-[rgb(220,38,38)] text-white text-[13px] font-semibold cursor-pointer hover:bg-[rgb(185,28,28)] transition-colors'
              onClick={handleConfirm}
            >
              {approveText ?? t('delete')}
            </button>
          </div>
        }
        onCancel={handleCancel}
      >
        <div className='flex flex-col py-2'>
          {title && (
            <span className='text-lg font-semibold text-[rgb(57,57,60)]'>
              {title}
            </span>
          )}
          {description && !Array.isArray(description) && (
            <span className='text-sm text-[rgb(115,123,140)] mt-1'>
              {description}
            </span>
          )}
          {description && Array.isArray(description) && (
            <div className='flex flex-col gap-1 mt-1'>
              {description.map((item, index) => (
                <span key={index} className='text-sm text-[rgb(115,123,140)]'>
                  {item}
                </span>
              ))}
            </div>
          )}
        </div>
      </Modal>
    );
  }

  return (
    <Modal
      title={getModalTitle}
      open={isOpen}
      centered
      styles={{
        content: { borderRadius: 16, padding: 24 },
        body: { padding: 0 },
        footer: { border: 'none', padding: 0, marginTop: 16 },
      }}
      footer={
        <div className='flex justify-end gap-3'>
          {cancelText && (
            <button
              type='button'
              className='h-9 px-4 rounded-lg border border-solid border-[rgb(232,230,227)] bg-white text-[rgb(57,57,60)] text-[13px] font-semibold cursor-pointer hover:bg-[rgb(245,245,243)] transition-colors'
              onClick={handleCancel}
            >
              {cancelText}
            </button>
          )}
          <button
            type='button'
            className='h-9 px-5 rounded-lg bg-[rgb(237,224,74)] text-[rgb(57,57,60)] text-[13px] font-semibold cursor-pointer hover:bg-[rgb(230,217,60)] transition-colors'
            onClick={handleConfirm}
          >
            {approveText ?? t('confirm')}
          </button>
        </div>
      }
      onCancel={handleCancel}
    >
      <div className='flex flex-col gap-6 w-full py-5 items-center'>
        {modalData[confirmType]?.icon}
        {content ?? (
          <>
            {title && (
              <span className='text-2xl font-semibold text-[rgb(57,57,60)]'>
                {title.toLocaleUpperCase()}
              </span>
            )}
            <div className='flex flex-col gap-2 w-full my-2'>
              {description && Array.isArray(description) ? (
                description.map((item, index) => (
                  <span key={index} className='text-base text-[rgb(57,57,60)]'>
                    {item}
                  </span>
                ))
              ) : (
                <span className='text-base text-[rgb(57,57,60)]'>{description}</span>
              )}
            </div>
          </>
        )}
      </div>
    </Modal>
  );
};
